// web/src/copilot/useSupabaseAuthHeaders.ts
//
// Shared hook used by both CopilotProvider and AskTapelensSheet's nested
// CopilotKit. Returns a synchronous getHeaders() callback that always
// produces the freshest Authorization: Bearer <jwt> header.
//
// CopilotKit's `headers` prop is sync (it can't await), so we keep the
// latest token in a useRef. Three sources keep the ref fresh:
//   1. Initial getSession() on mount.
//   2. onAuthStateChange subscription (Supabase fires TOKEN_REFRESHED
//      whenever it auto-refreshes — usually 5 min before expiry).
//   3. Defensive setInterval that calls refreshSession() every 5 minutes
//      in case the auto-refresh subscription misses an event (long-lived
//      tabs, browser throttling, etc.). Supabase JWTs default to 1h, so
//      5 min cadence gives ~12x headroom.
//
// This was extracted from CopilotProvider when we discovered that mounting
// the nested CopilotKit in AskTapelensSheet bypassed the outer provider's
// headers and 401-ed against FastAPI. Both consumers now use this hook.
import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 min

export function useSupabaseAuthHeaders(): () => Record<string, string> {
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // 1. Initial hydration from the existing session.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      tokenRef.current = data.session?.access_token ?? null;
    });

    // 2. Stay in sync with Supabase's own refresh events.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null;
    });

    // 3. Defensive periodic refresh — covers long-lived tabs where the
    //    auto-refresh subscription may not fire reliably.
    const intervalId = setInterval(() => {
      void supabase.auth.refreshSession().then(({ data, error }) => {
        if (cancelled || error) return;
        tokenRef.current = data.session?.access_token ?? tokenRef.current;
      });
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearInterval(intervalId);
    };
  }, []);

  return (): Record<string, string> => {
    const token = tokenRef.current;
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };
}
